/**
 * Create or edit a list group WITHOUT leaving the container editor — the
 * full List Groups editor card in a centered dialog. A new group is linked
 * to the container draft on save; an edited one stays linked (user,
 * 15/10/2026: editing must stay on the page, like creating). Its matches
 * are read on this container's campaign (19/10/2026 — tags are per campaign).
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';

import {
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
} from '@/components/ui';
import { ListGroupEditorCard, type ListGroup, useListGroupScope } from '@/features/list-groups';

interface Props {
  open: boolean;
  /** The group to edit; null = a new one. */
  group: ListGroup | null;
  countryKey: string;
  countryName: string;
  onClose: () => void;
  /** Called with a created group so the caller can link it. */
  onCreated: (group: ListGroup) => void;
}

export const ListGroupDialog: FC<Props> = ({
  open,
  group,
  countryKey,
  countryName,
  onClose,
  onCreated,
}) => {
  const { campaignWiserId } = useListGroupScope();
  if (!open) return null;
  return (
    <Drawer open onOpenChange={(next) => !next && onClose()}>
      <DrawerContent
        variant="center"
        width="w-[min(900px,92vw)]"
        className="max-h-[88vh]"
      >
        <DrawerHeader>
          <span className="text-sm font-semibold">
            {group
              ? `Edit list group — ${group.name} · shared: every container using it follows`
              : 'New list group — it will be linked to this container'}
            {campaignWiserId ? <span className="font-normal text-muted-foreground"> · matches read on this container&apos;s campaign</span> : null}
          </span>
        </DrawerHeader>
        <DrawerBody>
          <ListGroupEditorCard
            key={group?.id ?? 'new'}
            group={group}
            isNew={!group}
            countryKey={countryKey}
            countryName={countryName}
            onDone={(created) => {
              if (created && !group) onCreated(created);
              onClose();
            }}
          />
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};
